<template>
    <div>
        <div class="list-group mb-4">

            <!-- Group by tabs -->
            <template v-for="(groupedStatuses, tabName) in groupedByTab">
                <div class="tab-group" :key="tabName">
                    <div class="px-3 py-2 mt-2 d-flex justify-content-between">
                        <p class="font-weight-bold m-0">{{ tabName }}</p>
                        <a v-if="tabName !== 'Not in a tab'" :href="getTabSettingsUrl(tabName)" class="text-muted small">Manage</a>
                    </div>
                    <div v-for="status in groupedStatuses" :key="status.id" class="list-group-item bg-white">
                        <StatusRow
                            :status="status"
                            @edit="openEdit"
                            @delete="askDelete"
                        />
                        <b-alert
                            v-if="deleteErrors[status.id]"
                            show
                            variant="danger"
                            class="mt-2"
                            dismissible
                            @dismissed="clearError(status.id)"
                        >
                            <span v-html="deleteErrors[status.id]"></span>
                        </b-alert>
                        <EditForm
                            v-if="editId === status.id"
                            :tabs="tabs"
                            :status="status"
                            @save="editStatus"
                            @cancel="editId = null"
                        />
                    </div>
                </div>
            </template>

        </div>

        <b-button variant="outline" class="mt-2" @click="isNewFormOpen = true">
            <b-icon icon="plus-circle" class="text-primary"></b-icon> Add new status
        </b-button>

        <div v-if="isNewFormOpen">
            <hr />
            <validation-observer ref="observer" v-slot="{ passes }">
                <b-form @submit.prevent="passes(createStatus)" style="max-width: 500px;">
                    <b-form-group label="Name" label-for="new-name" class="mb-2">
                        <validation-provider name="name" rules="required|min:1|max:60" v-slot="{ errors }">
                            <b-form-input id="new-name" v-model="newStatusName"></b-form-input>
                            <span class="validation">{{ errors[0] }}</span>
                        </validation-provider>
                    </b-form-group>

                    <b-form-group label="Color" label-for="new-color" class="mb-2">
                        <validation-provider name="color" rules="hex" v-slot="{ errors }">
                            <b-form-input id="new-color" v-model="newStatusColor" type="color"></b-form-input>
                            <span class="validation">{{ errors[0] }}</span>
                        </validation-provider>
                        <small class="text-muted"><a href="#" @click.prevent="toggleHex = !toggleHex">Hex color</a>. Leave empty for automatic color.</small>
                    </b-form-group>
                    <div v-show="toggleHex">
                        <validation-provider name="color" rules="hex" v-slot="{ errors }">
                            <b-form-group label="Hex Color">
                                <b-form-input v-model="newStatusColor"></b-form-input>
                                <span class="validation">{{ errors[0] }}</span>
                            </b-form-group>
                        </validation-provider>
                        <small class="text-muted">See this <a target="_blank" href="https://htmlcolorcodes.com/">hex color finder</a> to search for a Hex color or get the RGB-equivalent.</small>
                    </div>

                    <b-form-group label="Display on tab" label-for="new-tab" class="mb-2">
                        <validation-provider name="tab" rules="" v-slot="{ errors }">
                            <b-form-select v-model="newTabId">
                                <option :value="null">Not on a tab</option>
                                <option v-for="tab in tabs" :key="tab.id" :value="tab.id">{{ tab.nav_title }}</option>
                            </b-form-select>
                            <span class="validation">{{ errors[0] }}</span>
                        </validation-provider>
                    </b-form-group>

                    <div class="d-flex justify-content-between mt-3">
                        <b-button variant="outline" @click="isNewFormOpen = false">
                            Cancel
                        </b-button>
                        <b-button type="submit" variant="primary">Create</b-button>
                    </div>
                </b-form>
            </validation-observer>
        </div>

        <b-modal
            id="delete-status-modal"
            title="Delete status"
            ok-title="Delete"
            cancel-variant="outline"
            ok-variant="danger"
            @ok="handleDeleteOk"
            :ok-disabled="isDeleteLoading">
            <div v-if="statusToDelete">
                <p>Are you sure you want to delete the status "{{ statusToDelete.name }}"?</p>
                <ul>
                    <li>Status history for this status will be removed.</li>
                </ul>

                <b-alert show variant="info" v-if="isDeleteLoading">
                    <img src="/loading.svg" style="height: 20px;" /> Loading connected posts...
                </b-alert>
                <b-alert show variant="info" v-else-if="connectedPostsCount > 0">
                    {{ connectedPostsCount }} post(s) will be moved to the new status.
                </b-alert>
                <b-alert show variant="info" v-else>
                    There are no posts in this status, so it can be safely removed.
                </b-alert>

                <b-form-group
                    v-if="!isDeleteLoading && connectedPostsCount > 0"
                    label="New Status"
                    :state="newStatusState"
                    invalid-feedback="Please select a new status">
                    <b-form-select
                        v-model="newStatusId"
                        :state="newStatusState">
                        <option :value="null" disabled>Select a new status</option>
                        <option
                            v-for="status in statuses"
                            :key="status.id"
                            :value="status.id"
                            v-if="status.id !== statusToDelete.id">
                            {{ status.name }}
                        </option>
                    </b-form-select>
                </b-form-group>
            </div>
        </b-modal>
    </div>
</template>

<script>
// StatusRow component
const StatusRow = {
    props: ['status'],
    template: `
        <div class="d-flex align-items-center justify-content-between py-2">
        <div class="d-flex align-items-center">
            <div class="d-inline-block mr-3"
                 :style="'height: 12px; width: 12px; border-radius: 50%; background-color: '+status.color">
            </div>
            <span class="font-weight-medium">{{ status.name }}</span>
        </div>
        <div class="d-flex align-items-center gap-3">
            <span class="text-muted small">ID: {{ status.id }}</span>
            <b-button variant="link" size="sm" class="px-2 mx-2 text-primary" :href="'/admin/posts/all?status_id='+status.id">
                <b-icon icon="eye" class="mr-1"></b-icon>
                Posts
            </b-button>
            <div class="btn-group">
                <b-button variant="outline" size="sm" class="px-2" @click="$emit('edit', status.id)">
                    <b-icon icon="pencil"></b-icon>
                </b-button>
                <b-button variant="outline" size="sm" class="px-2" @click="$emit('delete', status)">
                    <b-icon icon="trash"></b-icon>
                </b-button>
            </div>
        </div>
        </div>
    `
};

// EditForm component
const EditForm = {
    props: ['status', 'tabs'],
    data() {
        return {
            toggleHex: false,
            editedStatus: null
        }
    },
    created() {
        this.editedStatus = { ...this.status };
    },
    template: `
        <div>
            <hr />
            <validation-observer ref="observer" v-slot="{ passes }">
                <form @submit.prevent="passes(() => $emit('save', editedStatus))" style="max-width: 500px;">
                    <validation-provider name="name" rules="min:2|required" v-slot="{ errors }">
                        <b-form-group label="Name">
                            <b-form-input v-model="editedStatus.name"></b-form-input>
                            <span class="validation">{{ errors[0] }}</span>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider name="color" rules="required|hex" v-slot="{ errors }">
                        <b-form-group label="Color">
                            <b-form-input type="color" v-model="editedStatus.color"></b-form-input>
                            <span class="validation">{{ errors[0] }}</span>
                            <small class="text-muted"><a href="#" @click.prevent="toggleHex = !toggleHex">Hex color</a>.</small>
                        </b-form-group>
                    </validation-provider>
                    <div v-show="toggleHex">
                        <validation-provider name="color" rules="required|hex" v-slot="{ errors }">
                            <b-form-group label="Hex Color">
                                <b-form-input v-model="editedStatus.color"></b-form-input>
                                <span class="validation">{{ errors[0] }}</span>
                            </b-form-group>
                        </validation-provider>
                        <small class="text-muted">See this <a target="_blank" href="https://htmlcolorcodes.com/">hex color finder</a> to search for a Hex color or get the RGB-equivalent.</small>
                    </div>

                    <validation-provider name="tab" rules="" v-slot="{ errors }">
                        <b-form-group label="Display on tab">
                            <b-form-select v-model="editedStatus.tab_id">
                                <option :value="null">Not on a tab</option>
                                <option v-for="tab in tabs" :key="tab.id" :value="tab.id">{{ tab.nav_title }}</option>
                            </b-form-select>
                            <span class="validation">{{ errors[0] }}</span>
                        </b-form-group>
                    </validation-provider>

                    <b-button variant="outline" class="mt-2 mr-2" @click="$emit('cancel')">Cancel</b-button>
                    <b-button type="submit" variant="primary" class="mt-2">Save</b-button>
                </form>
            </validation-observer>
        </div>
    `
};

import NotificationPop from "../mixins/NotificationPop";
import { mapState } from "vuex";

export default {
    components: { StatusRow, EditForm },
    mixins: [NotificationPop],
    data() {
        return {
            statuses: [],
            tabs: [],
            statusToDelete: null,
            deleteErrors: {},
            statusToMove: null,
            editId: null,
            toggleHex: false,
            name: '',
            color: '',
            newStatusName: '',
            newStatusColor: null,
            isNewFormOpen: false,
            newTabId: null,
            newStatusId: null,
            newStatusState: null,
            connectedPostsCount: null,
            isDeleteLoading: true,
        };
    },
    computed: {
        ...mapState(['defaultStatusId']),
        defaultStatus() {
            return this.statuses.find(s => !s.tab_id);
        },
        getTabSettingsUrl() {
            return function(tabName) {
                const tab = this.tabs.find(t => t.nav_title === tabName);
                return `/admin/settings/tabs_statuses#tab_${tab ? tab.slug : ''}`;
            }
        },
        groupedByTab() {
            const groups = this.statuses.reduce((acc, status) => {
                const tabName = status.tab_id
                    ? this.tabs.find(t => t.id === status.tab_id)?.nav_title
                    : 'Not in a tab';

                if (!acc[tabName]) acc[tabName] = [];
                acc[tabName].push(status);

                return acc;
            }, {});

            // Convert groups to an array, sort with "Not in a tab" last, and return as an object
            const sortedGroups = Object.entries(groups).sort(([keyA], [keyB]) => {
                if (keyA === 'Not in a tab') return 1; // Place "Not in a tab" last
                if (keyB === 'Not in a tab') return -1; // Place others before "Not in a tab"
                return keyA.localeCompare(keyB); // Sort alphabetically otherwise
            });

            return Object.fromEntries(sortedGroups);
        },

    },
    methods: {
        showDeleteModal(status) {
            this.statusToDelete = status;
            this.newStatusId = null;
            this.newStatusState = null;
            this.connectedPostsCount = null;
            this.isDeleteLoading = true;

            let queryParams = new URLSearchParams({
                'status[]': status.id.toString(),
                limit: '1'
            });

            this.$bvModal.show('delete-status-modal');

            this.$http.get(`/http_api/posts?${queryParams.toString()}`)
                .then(({ data }) => {
                    this.connectedPostsCount = data.total;
                    this.isDeleteLoading = false;
                })
                .catch((error) => {
                    console.error('Error fetching connected posts count:', error);
                    this.isDeleteLoading = false;
                    this.connectedPostsCount = 'Unknown';
                });
        },
        handleDeleteOk(bvModalEvent) {
            if (this.connectedPostsCount > 0 && this.newStatusId === null) {
                bvModalEvent.preventDefault();
                this.newStatusState = false;
                return;
            }
            this.newStatusState = true;
            this.deleteStatus();
        },
        fetchStatuses() {
            this.$http.get(`/http_api/statuses`)
                .then((response) => {
                    this.statuses = response.data.data;
                })
                .catch((error) => {
                    this.popNotification('Failed to fetch statuses.', 'danger');
                });
        },
        editStatus(status) {
            this.$http.put(`/http_api/statuses/${this.editId}`, {
                name: status.name,
                color: status.color,
                tab_id: status.tab_id
            })
                .then((response) => {
                    this.popNotification('Status updated successfully', 'success');
                    this.editId = null;
                    this.fetchStatuses();
                })
                .catch((error) => {
                    if (error.response && error.response.data.errors && error.response.data.errors.name) {
                        this.popNotification(`Failed to create status. ${error.response.data.errors.name[0]}`, 'danger');
                    } else {
                        this.popNotification('Failed to create status.', 'danger');
                    }                });
        },
        askDelete(status) {
            this.$set(this.deleteErrors, status.id, null);
            const relatedTab = this.tabs.find(tab => tab.default_status_id === status.id);

            if (relatedTab) {
                this.$set(this.deleteErrors, status.id, `This status is used as the default status in the "${relatedTab.nav_title.trim()}" tab. <a href="/admin/settings/tabs_statuses#${relatedTab.slug}">Adjust it in the tab settings</a> at "Default Post Settings" before deleting.`);
                this.setErrorTimer(status.id);
                return;
            }

            if (this.defaultStatusId === status.id) {
                this.$set(this.deleteErrors, status.id, `This status is used as the default status for the whole portal. <a href="/admin/settings#posting">Adjust it in the portal settings</a> before deleting.`);
                this.setErrorTimer(status.id);
                return;
            }

            this.showDeleteModal(status);
        },
        deleteStatus() {
            const data = this.connectedPostsCount > 0 ? { new_status_id: this.newStatusId } : {};

            this.$http.delete(`/http_api/statuses/${this.statusToDelete.id}`, { data })
                .then((response) => {
                    this.statuses = this.statuses.filter(status => status.id !== this.statusToDelete.id);
                    this.statusToDelete = null;
                    this.newStatusId = null;
                    this.popNotification('Status deleted successfully', 'success');
                })
                .catch((error) => {
                    let errorMessage = 'Failed to delete status.';
                    if (error.response) {
                        switch (error.response.status) {
                            case 404:
                                errorMessage = 'Status not found. It may have been already deleted.';
                                break;
                            case 422:
                                errorMessage = error.response.data.message || 'Invalid input. Please check your data.';
                                break;
                            case 403:
                                errorMessage = 'You do not have permission to delete this status.';
                                break;
                            default:
                                errorMessage = `An error occurred: ${error.response.data.message || 'Unknown error'}`;
                        }
                    }
                    this.popNotification(errorMessage, 'danger');
                });
        },
        createStatus() {
            this.$http.post('/http_api/statuses', {
                name: this.newStatusName,
                color: this.newStatusColor,
                tab_id: this.newTabId,
            })
                .then((response) => {
                    this.statuses.push(response.data.data);
                    this.newStatusName = '';
                    this.newStatusColor = '';
                    this.isNewFormOpen = false;
                    this.popNotification('Status created successfully', 'success');
                })
                .catch((error) => {
                    if (error.response && error.response.data.errors && error.response.data.errors.name) {
                        this.popNotification(`Failed to create status. ${error.response.data.errors.name[0]}`, 'danger');
                    } else {
                        this.popNotification('Failed to create status.', 'danger');
                    }
                });
        },

        fetchTabs() {
            this.$http.get('/http_api/tabs')
                .then((response) => {
                    this.tabs = response.data.data;
                })
                .catch((error) => {
                    this.popNotification('Failed to fetch tabs.', 'danger');
                });
        },
        openEdit(statusId) {
            this.editId = statusId;
            const status = this.statuses.find(status => status.id === statusId);
            if (status) {
                this.name = status.name;
                this.color = status.color;
            }
        },
        setErrorTimer(statusId) {
            setTimeout(() => {
                this.clearError(statusId);
            }, 10000); // 10 seconds
        },

        clearError(statusId) {
            this.$set(this.deleteErrors, statusId, null);
        },
    },
    mounted() {
        this.fetchStatuses();
        this.fetchTabs();
    },
};
</script>
